/* eslint-disable @typescript-eslint/naming-convention, complexity, max-lines-per-function */
import {useContext, useMemo} from 'react';
import {NEXT_PUBLIC_MASTER_HOST} from 'core/helpers/env';
import {useListingContext} from 'plugins/modules/listing/context';
import {ICatalogSEO, IProductShort} from 'modules/products/types';
import {FilterContext} from 'plugins/modules/filter/context/new';
import {usePathname} from 'next/navigation';
import {useProductsTileViewOnTablet} from 'modules/products/hooks/useProductsTileViewOnTablet';
import {PAGES, STOCK_ROUTE} from 'core/modules/routing/constants';
import {useCurrentStockXData} from 'modules/stock-x/hooks/useCurrentStockXData';
import {useStockXBreadcrumbs} from 'modules/stock-x/hooks/useStockXBreadcrumbs';
import {metaData} from 'core/modules/PageHead/constants';
import {FilterPickupTodayButtonContext} from 'plugins/modules/filter/context/FilterPickupTodayButtonContext';
import {buildProductsSchemaJson, computeMinMaxPrices} from 'core/seo/schema/catalogSchemas';
import DOMPurify from 'isomorphic-dompurify';
import {IBreadcrumb} from 'modules/seo/types';
import {usePageViewGtag} from 'hooks/analytics/gtag/usePageViewGtag';
import {EventPortalName} from 'modules/google-analytics/constants';
import {NOINDEX_STOCK_CODES} from 'modules/stock-x/constants';

export const useStockXPageState = () => {
    usePageViewGtag('PROMO_PAGE', EventPortalName.Promotions);

    useProductsTileViewOnTablet();

    const pathname = usePathname();

    const {data: stockData} = useCurrentStockXData() ?? {};

    const {pagination, list, isFetching: isListFetching} = useListingContext<IProductShort>();
    const {isLoading: isFiltersLoading} = useContext(FilterContext);
    const {isExpressOrderLoading} = useContext(FilterPickupTodayButtonContext);

    const ogImage = `${NEXT_PUBLIC_MASTER_HOST}${list?.[0]?.media?.imagePreview || ''}`;
    const ogUrl = `${NEXT_PUBLIC_MASTER_HOST}${pathname}`;

    const {minPrice, maxPrice} = useMemo(() => computeMinMaxPrices(list), [list]);

    const productSchemaJson = useMemo(() => {
        return buildProductsSchemaJson({
            products: list,
            name: stockData?.title || '',
            description: stockData?.description || undefined,
            image: ogImage,
            offerCount: pagination?.totalCount || list?.length,
        });
    }, [list, stockData?.title, stockData?.description, pagination?.totalCount, ogImage]);

    const {code, title = '', tabs = []} = stockData ?? {};

    const initialBreadcrumbs: IBreadcrumb[] = useMemo(
        () => [
            {url: PAGES.HOME_PAGE.path, name: PAGES.HOME_PAGE.title},
            {url: PAGES.STOCK_PAGE.url, name: PAGES.STOCK_PAGE.title},
            {
                url: `${STOCK_ROUTE}/${code}`,
                name: title,
            },
        ],
        [code, title],
    );

    const {breadcrumbs: breadcrumbsList} = useStockXBreadcrumbs(initialBreadcrumbs);

    const seo: Partial<ICatalogSEO> = useMemo(
        () => ({
            pageTitle: stockData?.title ?? metaData.stock.discount.title,
            metaTitle: stockData?.title ?? metaData.stock.discount.title,
            metaDescription: stockData?.description
                ? DOMPurify.sanitize(stockData.description, {
                      ALLOWED_TAGS: [],
                      ALLOWED_ATTR: [],
                      // сохраняем текстовое содержимое
                      KEEP_CONTENT: true,
                      // возвращаем строку, а не DOM
                      RETURN_DOM: false,
                  }).trim()
                : metaData.stock.detail.description(),
            robots: code && NOINDEX_STOCK_CODES.includes(code) ? 'noindex' : undefined,
        }),
        [code, stockData?.description, stockData?.title],
    );

    return useMemo(
        () => ({
            isListFetching,
            isFiltersLoading: isFiltersLoading || isExpressOrderLoading,
            ogImage,
            ogUrl,
            productSchemaJson,
            seo,
            minPrice,
            maxPrice,
            pagination,
            breadcrumbsList,
            tabs,
            stockCode: code,
        }),
        [
            breadcrumbsList,
            isExpressOrderLoading,
            isFiltersLoading,
            isListFetching,
            maxPrice,
            minPrice,
            ogImage,
            ogUrl,
            pagination,
            productSchemaJson,
            seo,
            tabs,
            code,
        ],
    );
};
